import { Fragment, Slice, type Node as ProseMirrorNode, type Schema } from '@tiptap/pm/model';
import { Plugin, PluginKey } from '@tiptap/pm/state';

import { TiptapNodeType } from '../../tiptapTypes';
import { TASK_ITEM_TEXT_DEPTH } from './taskListItemTextRangeUtils';

/** The top level nodes of a slice, in order. */
const getSliceNodes = (slice: Slice): ProseMirrorNode[] => {
    const nodes: ProseMirrorNode[] = [];

    slice.content.forEach((node) => nodes.push(node));

    return nodes;
};

/** The task items held directly by a task list node. */
const getTaskItems = (taskList: ProseMirrorNode): ProseMirrorNode[] => {
    const taskItems: ProseMirrorNode[] = [];

    taskList.forEach((taskItem) => taskItems.push(taskItem));

    return taskItems;
};

/**
 * Builds a task item holding the title's text. The title is plain text only, so nothing can be
 * lost turning it into a task's paragraph.
 */
const createTaskItemFromTitle = (title: ProseMirrorNode, schema: Schema): ProseMirrorNode =>
    schema.nodes[TiptapNodeType.taskItem].create(
        null,
        schema.nodes[TiptapNodeType.paragraph].create(null, title.content),
    );

/**
 * Pastes a copied list's title as an ordinary task rather than as the row the rest of the list
 * hangs off.
 *
 * A copy of a whole task list element carries its title, and `taskListTitle` cannot live inside a
 * task item. Left alone, ProseMirror puts the title's text in the task holding the caret and nests
 * the entire copied list beneath it. Rewriting the title into a leading task item first makes the
 * slice a single task list, so it lands as plain siblings - the same as pasting an untitled copy.
 */
export const createTaskListTitlePasteConversionPlugin = (): Plugin =>
    new Plugin({
        key: new PluginKey('taskListTitlePasteConversion'),
        props: {
            transformPasted: (slice, view) => {
                const [titleNode, ...taskListNodes] = getSliceNodes(slice);

                if (titleNode?.type.name !== TiptapNodeType.taskListTitle) return slice;
                if (!taskListNodes.length) return slice;
                if (taskListNodes.some((node) => node.type.name !== TiptapNodeType.taskList)) return slice;

                const { schema } = view.state;
                const taskItems = [
                    // An untyped title would otherwise arrive as a blank task.
                    ...(titleNode.content.size ? [createTaskItemFromTitle(titleNode, schema)] : []),
                    ...taskListNodes.flatMap(getTaskItems),
                ];

                if (!taskItems.length) return slice;

                // Open to a task's own text at the start, so the first task merges into the caret's
                // task exactly as an untitled copy does. The end of the slice is untouched, so its
                // depth carries over.
                return new Slice(
                    Fragment.from(schema.nodes[TiptapNodeType.taskList].create(null, taskItems)),
                    TASK_ITEM_TEXT_DEPTH,
                    slice.openEnd,
                );
            },
        },
    });
